/**
 * siteBoundaryGeometry — geometry helpers for the multi-parcel site
 * boundary model (MP pack).
 *
 * Conventions (per CLAUDE.md unit-consistency rule, mirroring
 * `services/siteLayersBlock.ts`):
 *   - Public API takes/returns DEGREES for lat/lng.
 *   - Areas in acres (1 ac = 4_046.8564224 m²).
 *   - Perimeters in km.
 *   - Distances internally in metres; Haversine on WGS-84 mean radius.
 *
 * Why a fresh module rather than extending `siteLayersBlock.ts`:
 *   - `siteLayersBlock.ts` is rendering-focused (m²/m output formatters
 *     keyed to the LLM prompt-block thresholds).
 *   - This module is data-focused (acres + km output for persisted SED /
 *     site fields, with an aggregate type that downstream consumers read
 *     straight off).
 *   - Keeping them separate avoids accidentally coupling the prompt
 *     formatter cross-overs to the persisted area unit.
 *
 * Disjoint detection — pairwise minimum vertex-to-vertex Haversine. This
 * is an O(N·M) approximation, not a true polygon proximity (segment-to-
 * segment distance would be O(N·M) with a higher constant). The 100 m
 * threshold is generous enough that vertex-sampling misses are rare for
 * real-world parcels, and the cost matters because the helper runs on
 * every site read until the canonical shape persists. A bounding-box
 * prefilter (sound Haversine LOWER bounds — `haversineLowerBoundMeters`)
 * settles far-apart pairs without visiting a vertex and trims every
 * remaining scan to the vertices near the other parcel's box; the
 * classification is identical to the exhaustive scan (see
 * `siteBoundaryGeometry.disjointPrefilter.test.ts`).
 */

import type { SiteBoundaryParcel, SiteBoundaryAggregate } from '../types/siteBoundary';
import polygonClipping from 'polygon-clipping';
import { deterministicSin } from './deterministicSin';
import {
  OVERLAP_AREA_THRESHOLD_ACRES,
  SITE_BOUNDARY_AGGREGATE_VERSION,
} from './siteParcelClassification';

/* ─── Named geometry constants (no magic numbers) ──────────────────── */

/** Mean Earth radius (WGS-84 mean radius, IUGG 1980), metres. */
const EARTH_RADIUS_M = 6_371_000;
/** Degrees → radians. */
const DEG_TO_RAD = Math.PI / 180;
/** Metres → kilometres. */
const M_PER_KM = 1_000;
/** Metres² → acres. */
const M2_PER_ACRE = 4_046.8564224;
/**
 * Disjoint threshold, metres. Two parcels are flagged "disjoint" when
 * the nearest pair of vertices between any pair of parcels is more than
 * this far apart. 100 m is large enough to ignore digitizing slop on
 * abutting parcels but tight enough to flag the "datacenter parcel +
 * substation parcel 1 km down the road" case the user reported.
 */
const DISJOINT_THRESHOLD_M = 100;
/**
 * Slack added to the disjoint threshold before a bounding-box lower bound
 * may prune a vertex scan. The bound is mathematically ≤ the true Haversine
 * distance, so the slack only absorbs floating-point noise in the two
 * evaluations; 1 mm cannot move a classification made at 100 m.
 */
const BBOX_PREFILTER_SLACK_M = 1e-3;

/* ─── Defensive vertex guard ───────────────────────────────────────── */

/**
 * HOTFIX(lat-crash) — runtime type-guard for a usable lat/lng vertex.
 *
 * `polygon` / `centroid` are typed required `{lat:number;lng:number}`, but
 * persisted / legacy KMZ docs are NOT runtime-validated — a parcel can arrive
 * with `polygon: undefined`, a nullish vertex (`polygon: [v0, undefined, v2]`),
 * or a non-finite coordinate (`NaN`/`Infinity` from a malformed import). Every
 * vertex-reading helper below funnels through this guard so reading `.lat`/`.lng`
 * can never throw "Cannot read properties of undefined (reading 'lat')" and a
 * NaN coordinate can never poison an area/centroid/bbox computation.
 *
 * Guard only — for already-valid data it accepts every vertex, so output is
 * byte-for-byte identical to the pre-guard behaviour.
 */
export function isFiniteLatLng(p: unknown): p is { lat: number; lng: number } {
  return (
    !!p &&
    typeof p === 'object' &&
    Number.isFinite((p as { lat?: unknown }).lat) &&
    Number.isFinite((p as { lng?: unknown }).lng)
  );
}

/**
 * HOTFIX(lat-crash) — does this site carry ANY usable lat/lng anchor?
 *
 * `SiteEntity.location` is typed required `{lat:number;lng:number}`, but a
 * partial / orphan stub doc (e.g. the 2-key record a cancelled report
 * generation leaves behind) arrives with `location` / `siteCentroid` /
 * `siteBoundaryAggregate.aggregateCentroid` / `siteBoundary[]` all undefined.
 * Such a site renders as a blank list card, and any unguarded `.location.lat`
 * read on the click → landing path threw
 * "Cannot read properties of undefined (reading 'lat')".
 *
 * Callers use this to render a graceful "incomplete / location not set" state
 * (list card note + landing panel) instead of mounting a map onto an undefined
 * centre. A site is "usable" when any of these resolves to a finite coordinate:
 *   - `location`
 *   - `siteCentroid`
 *   - `siteBoundaryAggregate.aggregateCentroid`
 *   - any finite vertex of `siteBoundary[]`
 *
 * Pure / synchronous; never throws on a malformed input.
 */
export function siteHasUsableLocation(site: {
  location?: { lat?: unknown; lng?: unknown } | null;
  siteCentroid?: { lat?: unknown; lng?: unknown } | null;
  siteBoundaryAggregate?: { aggregateCentroid?: { lat?: unknown; lng?: unknown } | null } | null;
  siteBoundary?: ReadonlyArray<{ lat: number; lng: number }> | null;
} | null | undefined): boolean {
  if (!site) return false;
  return (
    isFiniteLatLng(site.location) ||
    isFiniteLatLng(site.siteCentroid) ||
    isFiniteLatLng(site.siteBoundaryAggregate?.aggregateCentroid) ||
    cleanVertices(site.siteBoundary).length > 0
  );
}

/**
 * HOTFIX(lat-crash) — return only the finite lat/lng vertices of a (possibly
 * undefined / nullish-element-bearing) polygon. A non-array input yields `[]`.
 */
function cleanVertices(
  polygon: ReadonlyArray<{ lat: number; lng: number }> | null | undefined,
): Array<{ lat: number; lng: number }> {
  if (!Array.isArray(polygon)) return [];
  return polygon.filter(isFiniteLatLng);
}

function cleanHoles(
  holes: ReadonlyArray<ReadonlyArray<{ lat: number; lng: number }>> | null | undefined,
): Array<Array<{ lat: number; lng: number }>> {
  if (!Array.isArray(holes)) return [];
  return holes
    .map((ring) => cleanVertices(ring))
    .filter((ring) => ring.length >= 3);
}

/* ─── Public helpers ───────────────────────────────────────────────── */

/**
 * Great-circle distance between two lat/lng points using the Haversine
 * formula. Returns metres.
 *
 * Numerically stable form — clamps the sqrt argument to [0, 1] before
 * asin to avoid NaN from floating-point overshoot at very small
 * distances (mirrors `siteLayersBlock.haversineMeters`).
 */
export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const dLat = (b.lat - a.lat) * DEG_TO_RAD;
  const dLng = (b.lng - a.lng) * DEG_TO_RAD;
  const lat1 = a.lat * DEG_TO_RAD;
  const lat2 = b.lat * DEG_TO_RAD;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Planimetric polygon area in ACRES via the spherical-excess shoelace
 * formula:
 *   A = R² · |Σ (λ₂ − λ₁) · (2 + sin φ₁ + sin φ₂)| / 2
 *
 * The ring is treated as closed (last vertex implicitly wraps to the
 * first). Sign of the shoelace indicates winding order; we take `abs`
 * because callers don't care about CW vs CCW.
 *
 * Accuracy ~0.1 % at parcel scale — the limiting factor is the input
 * (KMZ / hand-drawn vertices are themselves ±metre-scale).
 *
 * Returns 0 for degenerate input (< 3 vertices).
 *
 * The area is hash material for the study-area and Generation & Impact
 * boundary hashes, which are made in the browser and checked on Cloud Run, so
 * it must be the same bits on every CPU and JS engine: sin is
 * `deterministicSin` (x86_64 V8's values everywhere), never Math.sin, and the
 * arithmetic order here must not change.
 */
export function polygonArea(polygonInput: ReadonlyArray<{ lat: number; lng: number }>): number {
  // HOTFIX(lat-crash): drop nullish / non-finite vertices first so a degenerate
  // KMZ vertex can't throw on `.lng`/`.lat` here (or NaN-poison the area).
  const polygon = cleanVertices(polygonInput);
  if (polygon.length < 3) return 0;
  let acc = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i]!;
    const b = polygon[(i + 1) % polygon.length]!;
    acc +=
      (b.lng - a.lng) * DEG_TO_RAD *
      (2 + deterministicSin(a.lat * DEG_TO_RAD) + deterministicSin(b.lat * DEG_TO_RAD));
  }
  const m2 = Math.abs((acc * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
  return m2 / M2_PER_ACRE;
}

/**
 * Perimeter of a polygon ring in KM — sum of haversine segments around
 * the ring (closing wrap is implicit).
 */
export function polygonPerimeter(polygonInput: ReadonlyArray<{ lat: number; lng: number }>): number {
  // HOTFIX(lat-crash): drop nullish / non-finite vertices so a degenerate
  // KMZ vertex can't throw inside haversineMeters (which reads `.lat`/`.lng`).
  const polygon = cleanVertices(polygonInput);
  if (polygon.length < 2) return 0;
  let sum = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i]!;
    const b = polygon[(i + 1) % polygon.length]!;
    sum += haversineMeters(a, b);
  }
  return sum / M_PER_KM;
}

/**
 * Planimetric parcel area in ACRES subtracting any interior hole rings.
 * Identical to `polygonArea(polygonInput)` when `holesInput` is empty/omitted.
 */
export function parcelArea(
  polygonInput: ReadonlyArray<{ lat: number; lng: number }>,
  holesInput?: ReadonlyArray<ReadonlyArray<{ lat: number; lng: number }>> | null,
): number {
  const outer = polygonArea(polygonInput);
  const holes = cleanHoles(holesInput);
  if (holes.length === 0) return outer;
  let holeSum = 0;
  for (const h of holes) {
    holeSum += polygonArea(h);
  }
  return Math.max(0, outer - holeSum);
}

/**
 * Parcel perimeter in KM including any interior hole rings.
 * Identical to `polygonPerimeter(polygonInput)` when `holesInput` is empty/omitted.
 */
export function parcelPerimeter(
  polygonInput: ReadonlyArray<{ lat: number; lng: number }>,
  holesInput?: ReadonlyArray<ReadonlyArray<{ lat: number; lng: number }>> | null,
): number {
  const outer = polygonPerimeter(polygonInput);
  const holes = cleanHoles(holesInput);
  if (holes.length === 0) return outer;
  let holeSum = 0;
  for (const h of holes) {
    holeSum += polygonPerimeter(h);
  }
  return outer + holeSum;
}

/**
 * Polygon centroid as the arithmetic mean of vertices. A parcel-scale
 * approximation — the true geometric centroid would weight each segment
 * by length — sufficient for our use cases (provider-call centre point,
 * map framing, prompt block hint). Mirrors the existing
 * `centroidOf` helper in `SiteLocationBoundaryEditor.tsx`.
 *
 * Returns `{lat:0, lng:0}` for an empty polygon (or one with no usable
 * vertices) — callers should guard if they need to distinguish "no polygon"
 * from "polygon at null island".
 */
export function polygonCentroid(polygonInput: ReadonlyArray<{ lat: number; lng: number }>): { lat: number; lng: number } {
  // HOTFIX(lat-crash): drop nullish / non-finite vertices first. The prior
  // computeAggregate fallback calls THIS on a raw `p.polygon`, so a single
  // nullish vertex here threw "reading 'lat'" — exactly the crash class we are
  // closing. Filtering keeps the maths identical for valid polygons.
  const polygon = cleanVertices(polygonInput);
  if (polygon.length === 0) return { lat: 0, lng: 0 };
  let lat = 0;
  let lng = 0;
  for (const p of polygon) {
    lat += p.lat;
    lng += p.lng;
  }
  return { lat: lat / polygon.length, lng: lng / polygon.length };
}

function ringToLngLatRing(ring: ReadonlyArray<{ lat: number; lng: number }>): [number, number][] {
  // HOTFIX(lat-crash): drop nullish / non-finite vertices so `p.lng`/`p.lat`
  // can't throw, and a NaN coordinate can't corrupt the polygon-clipping input.
  const pts: [number, number][] = cleanVertices(ring).map(p => [p.lng, p.lat]);
  if (pts.length >= 3) {
    const first = pts[0]!;
    const last = pts[pts.length - 1]!;
    if (first[0] !== last[0] || first[1] !== last[1]) {
      pts.push([first[0], first[1]]);
    }
  }
  return pts;
}

function multiPolygonAreaAcres(multi: polygonClipping.MultiPolygon): number {
  let acres = 0;
  for (const poly of multi) {
    const outer = poly[0];
    if (!outer || outer.length < 3) continue;
    acres += polygonArea(outer.map(([lng, lat]) => ({ lat, lng })));
    for (let hi = 1; hi < poly.length; hi++) {
      const hole = poly[hi];
      if (hole && hole.length >= 3) {
        acres -= polygonArea(hole.map(([lng, lat]) => ({ lat, lng })));
      }
    }
  }
  return Math.max(0, acres);
}

function parcelToClippingPolygon(p: {
  polygon: ReadonlyArray<{ lat: number; lng: number }>;
  holes?: ReadonlyArray<ReadonlyArray<{ lat: number; lng: number }>> | null;
}): polygonClipping.Polygon {
  const holes = cleanHoles(p.holes);
  return [
    ringToLngLatRing(p.polygon),
    ...holes.map((h) => ringToLngLatRing(h)),
  ] as polygonClipping.Polygon;
}

/**
 * Union area across all parcel polygons in acres. Overlapping regions are
 * counted once, and interior holes are subtracted. Disjoint parcels sum
 * naturally; single-parcel sites defer to `parcelArea`.
 */
export function unionPolygonAreaAcres(parcels: ReadonlyArray<SiteBoundaryParcel>): number {
  // HOTFIX(lat-crash): `p.polygon` can be undefined on a malformed/legacy doc,
  // so reading `.length` directly threw "reading 'length'". Use the cleaned
  // vertex count (also drops nullish vertices that would crash downstream).
  const valid = parcels.filter(p => cleanVertices(p.polygon).length >= 3);
  if (valid.length === 0) return 0;
  if (valid.length === 1) return parcelArea(valid[0]!.polygon, valid[0]!.holes);

  const geoms = valid.map(p => parcelToClippingPolygon(p));
  const unionResult = unionMultiPolygon(geoms);
  return unionResult ? multiPolygonAreaAcres(unionResult) : 0;
}

function lngLatRingToLatLng(outer: ReadonlyArray<[number, number]>): Array<{ lat: number; lng: number }> {
  const ring = outer.map(([lng, lat]) => ({ lat, lng }));
  if (
    ring.length > 1
    && ring[0]!.lat === ring[ring.length - 1]!.lat
    && ring[0]!.lng === ring[ring.length - 1]!.lng
  ) {
    ring.pop();
  }
  return ring;
}

/**
 * Union every ring, folding one parcel at a time: `union(acc, next)`.
 *
 * Deliberately NOT the variadic single-sweep form. Measured on a dense
 * cluster of 60 heavily overlapping 400-vertex parcels (the production
 * shape this runs on), one sweep over every operand took ~3.5× longer than
 * the fold (151 ms vs 42 ms): the fold's accumulated result simplifies as it
 * grows, so each later union meets far fewer segment intersections than a
 * single pass over all 60 rings at once. Both produce the same union area
 * (`siteBoundaryGeometry.disjointPrefilter.test.ts` pins it).
 */
function unionMultiPolygon(
  geoms: polygonClipping.Polygon[],
): polygonClipping.MultiPolygon | null {
  if (geoms.length === 0) return null;
  let unionResult = geoms[0] as unknown as polygonClipping.MultiPolygon;
  for (let i = 1; i < geoms.length; i++) {
    unionResult = polygonClipping.union(unionResult, geoms[i]!) as polygonClipping.MultiPolygon;
  }
  return unionResult;
}

/**
 * Union parcel rings into a single outer boundary (largest-area component wins
 * when the union is a MultiPolygon).
 */
export function unionParcelPolygons(
  parcels: ReadonlyArray<Pick<SiteBoundaryParcel, 'polygon'>>,
): Array<{ lat: number; lng: number }> | null {
  // HOTFIX(lat-crash): guard against `p.polygon === undefined` (reads `.length`)
  // and nullish vertices via the cleaned vertex count.
  const valid = parcels.filter(p => cleanVertices(p.polygon).length >= 3);
  if (valid.length === 0) return null;
  // HOTFIX(lat-crash 3/3): the single-parcel fast path used to return the RAW
  // `p.polygon` — so a parcel with ≥3 valid vertices but ALSO a nullish vertex
  // (which still passes the `>= 3` filter above) leaked that nullish vertex
  // through to the caller. `mergeParcelsLocal` then PERSISTS this ring via
  // `store.updateParcelPolygon`, baking the crash into the doc, and any consumer
  // reading `.lat` on the returned ring threw "reading 'lat'". Return the
  // CLEANED ring so the single-parcel output is crash-safe; for an
  // already-valid parcel `cleanVertices` is the identity, so output is
  // unchanged.
  if (valid.length === 1) return cleanVertices(valid[0]!.polygon);

  const geoms = valid.map(p => [ringToLngLatRing(p.polygon)] as polygonClipping.Polygon);
  const unionResult = unionMultiPolygon(geoms);
  if (!unionResult || unionResult.length === 0) return null;

  let bestOuter: [number, number][] | null = null;
  let bestArea = -1;
  for (const poly of unionResult) {
    const outer = poly[0];
    if (!outer || outer.length < 3) continue;
    const area = polygonArea(outer.map(([lng, lat]) => ({ lat, lng })));
    if (area > bestArea) {
      bestArea = area;
      bestOuter = outer;
    }
  }
  return bestOuter ? lngLatRingToLatLng(bestOuter) : null;
}

const MIN_SPLIT_AREA_ACRES = 0.01;

function polygonBbox(polygon: ReadonlyArray<{ lat: number; lng: number }>): {
  south: number;
  west: number;
  north: number;
  east: number;
} {
  let south = Number.POSITIVE_INFINITY;
  let north = Number.NEGATIVE_INFINITY;
  let west = Number.POSITIVE_INFINITY;
  let east = Number.NEGATIVE_INFINITY;
  for (const v of polygon) {
    if (v.lat < south) south = v.lat;
    if (v.lat > north) north = v.lat;
    if (v.lng < west) west = v.lng;
    if (v.lng > east) east = v.lng;
  }
  return { south, west, north, east };
}

/** Half-plane wedge large enough to cover the parcel bbox on one side of line a→b. */
function halfPlaneWedge(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
  keepPositive: boolean,
  bbox: ReturnType<typeof polygonBbox>,
): polygonClipping.Polygon {
  const pad = Math.max(bbox.north - bbox.south, bbox.east - bbox.west, 0.0005) * 20;
  const dLat = b.lat - a.lat;
  const dLng = b.lng - a.lng;
  const len = Math.hypot(dLat, dLng) || 1e-12;
  const tLat = dLat / len;
  const tLng = dLng / len;
  const nLat = tLng;
  const nLng = -tLat;
  const sign = keepPositive ? 1 : -1;
  const extent = pad * 2;
  const off = pad * sign;

  const la = { lat: a.lat - tLat * extent, lng: a.lng - tLng * extent };
  const lb = { lat: b.lat + tLat * extent, lng: b.lng + tLng * extent };
  const p1 = la;
  const p2 = lb;
  const p3 = { lat: lb.lat + nLat * off, lng: lb.lng + nLng * off };
  const p4 = { lat: la.lat + nLat * off, lng: la.lng + nLng * off };

  return [ringToLngLatRing([p1, p2, p3, p4])];
}

function multiPolygonToLatLngRings(multi: polygonClipping.MultiPolygon): Array<Array<{ lat: number; lng: number }>> {
  const out: Array<Array<{ lat: number; lng: number }>> = [];
  for (const poly of multi) {
    const outer = poly[0];
    if (!outer || outer.length < 3) continue;
    const ring = lngLatRingToLatLng(outer);
    if (ring.length >= 3) out.push(ring);
  }
  return out;
}

/**
 * Split a parcel polygon along the line from `a` to `b`. Returns two polygons
 * when the cut succeeds; null when degenerate.
 */
export function splitPolygonByLine(
  polygonInput: ReadonlyArray<{ lat: number; lng: number }>,
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): [Array<{ lat: number; lng: number }>, Array<{ lat: number; lng: number }>] | null {
  // HOTFIX(lat-crash 3/3): the raw-length `polygon.length < 3` guard let a
  // length-3 ring with a NULLISH vertex (`[v0, undefined, v2]`) through, and
  // `polygonBbox(polygon)` below then read `.lat` on the undefined vertex →
  // "Cannot read properties of undefined (reading 'lat')". `ringToLngLatRing`
  // was already vertex-safe but `polygonBbox` was not. Clean once at the entry
  // so BOTH the count guard and the bbox operate on finite vertices. For an
  // already-valid polygon `cleanVertices` is the identity, so the split result
  // is unchanged.
  const polygon = cleanVertices(polygonInput);
  if (polygon.length < 3) return null;
  if (Math.hypot(b.lat - a.lat, b.lng - a.lng) < 1e-9) return null;

  const parcelGeom = [ringToLngLatRing(polygon)] as polygonClipping.Polygon;
  const bbox = polygonBbox(polygon);
  const wedgeA = halfPlaneWedge(a, b, true, bbox);
  const wedgeB = halfPlaneWedge(a, b, false, bbox);

  const partA = polygonClipping.intersection(parcelGeom, wedgeA) as polygonClipping.MultiPolygon;
  const partB = polygonClipping.intersection(parcelGeom, wedgeB) as polygonClipping.MultiPolygon;

  const ringsA = multiPolygonToLatLngRings(partA);
  const ringsB = multiPolygonToLatLngRings(partB);
  if (ringsA.length === 0 || ringsB.length === 0) return null;

  const polyA = ringsA.sort((x, y) => polygonArea(y) - polygonArea(x))[0]!;
  const polyB = ringsB.sort((x, y) => polygonArea(y) - polygonArea(x))[0]!;
  if (polygonArea(polyA) < MIN_SPLIT_AREA_ACRES || polygonArea(polyB) < MIN_SPLIT_AREA_ACRES) {
    return null;
  }
  return [polyA, polyB];
}

function sumParcelAreas(parcels: ReadonlyArray<SiteBoundaryParcel>): number {
  return parcels.reduce((s, p) => s + (Number.isFinite(p.area) ? p.area : 0), 0);
}

/* ─── Disjoint detection — bounding-box prefilter + vertex scan ─────── */

/** Axis-aligned lat/lng box, degrees. */
export interface LatLngBbox {
  south: number;
  west: number;
  north: number;
  east: number;
}

function verticesBbox(vertices: ReadonlyArray<{ lat: number; lng: number }>): LatLngBbox | null {
  if (vertices.length === 0) return null;
  let south = Number.POSITIVE_INFINITY;
  let north = Number.NEGATIVE_INFINITY;
  let west = Number.POSITIVE_INFINITY;
  let east = Number.NEGATIVE_INFINITY;
  for (const v of vertices) {
    if (v.lat < south) south = v.lat;
    if (v.lat > north) north = v.lat;
    if (v.lng < west) west = v.lng;
    if (v.lng > east) east = v.lng;
  }
  return { south, west, north, east };
}

/** Gap between a value and a closed interval — 0 when inside. */
function intervalGap(value: number, lo: number, hi: number): number {
  if (value < lo) return lo - value;
  if (value > hi) return value - hi;
  return 0;
}

/** Gap between two closed intervals — 0 when they overlap or touch. */
function intervalsGap(aLo: number, aHi: number, bLo: number, bHi: number): number {
  return Math.max(0, Math.max(aLo, bLo) - Math.min(aHi, bHi));
}

/**
 * Sound LOWER bound, metres, on the Haversine distance between any two
 * points whose latitudes differ by at least `dLatDeg`, whose longitudes
 * differ by at least `dLngDeg`, and which both lie within `maxAbsLatDeg` of
 * the equator.
 *
 * Derivation — the Haversine identity used by `haversineMeters`,
 *   hav(d/R) = hav(Δφ) + cos φ₁ · cos φ₂ · hav(Δλ),
 * is monotone in each term on [0, π], so replacing Δφ and Δλ by their
 * minimum values and cos φ₁ · cos φ₂ by its minimum cos²(φmax) can only
 * LOWER the right-hand side. Hence the result never exceeds the true
 * distance of any point pair satisfying the premises — which is what makes
 * pruning on it exact rather than heuristic.
 *
 * Longitude wrap-around: `hav(Δλ)` is periodic, so a raw gap of 350° is the
 * same as 10°. A gap of ≥ 180° therefore carries no information about the
 * short way round and contributes nothing (the bound stays sound, merely
 * weaker). Latitude cannot wrap.
 */
export function haversineLowerBoundMeters(dLatDeg: number, dLngDeg: number, maxAbsLatDeg: number): number {
  const dLat = Math.abs(dLatDeg) * DEG_TO_RAD;
  const dLngAbs = Math.abs(dLngDeg);
  const dLng = dLngAbs >= 180 ? 0 : dLngAbs * DEG_TO_RAD;
  const cosMax = Math.cos(Math.min(90, Math.abs(maxAbsLatDeg)) * DEG_TO_RAD);
  const h = Math.sin(dLat / 2) ** 2 + cosMax * cosMax * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Lower bound on the distance from `p` to ANY point inside `box`. */
export function pointToBboxLowerBoundMeters(
  p: { lat: number; lng: number },
  box: LatLngBbox,
): number {
  return haversineLowerBoundMeters(
    intervalGap(p.lat, box.south, box.north),
    intervalGap(p.lng, box.west, box.east),
    Math.max(Math.abs(p.lat), Math.abs(box.south), Math.abs(box.north)),
  );
}

/** Lower bound on the distance between ANY point of `a` and ANY point of `b`. */
export function bboxGapLowerBoundMeters(a: LatLngBbox, b: LatLngBbox): number {
  return haversineLowerBoundMeters(
    intervalsGap(a.south, a.north, b.south, b.north),
    intervalsGap(a.west, a.east, b.west, b.east),
    Math.max(Math.abs(a.south), Math.abs(a.north), Math.abs(b.south), Math.abs(b.north)),
  );
}

/** Counters from `detectDisjointWithStats` — lets tests PROVE the prefilter prunes. */
export interface DisjointScanStats {
  /** Parcel pairs examined (both parcels had ≥ 1 usable vertex). */
  pairs: number;
  /** Pairs settled by a bounding-box bound alone — no vertex pair compared. */
  pairsPrunedByBbox: number;
  /** Haversine evaluations between candidate vertices. */
  vertexPairsCompared: number;
}

/** Anything further apart than this (by a sound lower bound) can be pruned. */
const DISJOINT_PRUNE_ABOVE_M = DISJOINT_THRESHOLD_M + BBOX_PREFILTER_SLACK_M;

function scanDisjoint(
  parcels: ReadonlyArray<SiteBoundaryParcel>,
  stats: DisjointScanStats | null,
): boolean {
  if (parcels.length < 2) return false;
  // HOTFIX(lat-crash): `polygon` can be undefined / hold nullish vertices;
  // clean first so the `.lat`/`.lng` reads below can't throw. Cleaned and
  // boxed ONCE per parcel — the previous scan re-cleaned parcel `j` inside
  // the `i` loop, i.e. N times over.
  const prepared = parcels.map((p) => {
    const vertices = cleanVertices(p?.polygon);
    const bbox = verticesBbox(vertices);
    return bbox ? { vertices, bbox } : null;
  });
  for (let i = 0; i < prepared.length; i++) {
    const a = prepared[i];
    if (!a) continue;
    for (let j = i + 1; j < prepared.length; j++) {
      const b = prepared[j];
      if (!b) continue;
      if (stats) stats.pairs += 1;
      // Pair-level prefilter: boxes further apart than the threshold put
      // EVERY vertex pair further apart too → this pair is disjoint, and so
      // is the site. Not one vertex is visited.
      if (bboxGapLowerBoundMeters(a.bbox, b.bbox) > DISJOINT_PRUNE_ABOVE_M) {
        if (stats) stats.pairsPrunedByBbox += 1;
        return true;
      }
      // Vertex-level prefilter: a vertex further than the threshold from the
      // OTHER parcel's box cannot be within the threshold of any of its
      // vertices, so only the rim facing the other box is scanned. For two
      // abutting parcels that is the shared seam, not the whole ring.
      const candA = a.vertices.filter((v) => pointToBboxLowerBoundMeters(v, b.bbox) <= DISJOINT_PRUNE_ABOVE_M);
      const candB = b.vertices.filter((v) => pointToBboxLowerBoundMeters(v, a.bbox) <= DISJOINT_PRUNE_ABOVE_M);
      if (candA.length === 0 || candB.length === 0) {
        if (stats) stats.pairsPrunedByBbox += 1;
        return true;
      }
      // Exact scan over the survivors — same comparison as before, same
      // early exit at the first close pair.
      let close = false;
      outer: for (const va of candA) {
        for (const vb of candB) {
          if (stats) stats.vertexPairsCompared += 1;
          if (haversineMeters(va, vb) <= DISJOINT_THRESHOLD_M) {
            close = true;
            break outer;
          }
        }
      }
      if (!close) return true;
    }
  }
  return false;
}

/**
 * `true` when any two parcels are non-contiguous (nearest pairwise
 * vertex Haversine > DISJOINT_THRESHOLD_M). Single-parcel sites are
 * never disjoint (vacuously — no second parcel to be apart from).
 */
export function detectDisjoint(parcels: ReadonlyArray<SiteBoundaryParcel>): boolean {
  return scanDisjoint(parcels, null);
}

/**
 * `detectDisjoint` plus the scan counters. Diagnostic / test hook — the
 * classification is byte-identical to `detectDisjoint`.
 */
export function detectDisjointWithStats(
  parcels: ReadonlyArray<SiteBoundaryParcel>,
): { isDisjoint: boolean; stats: DisjointScanStats } {
  const stats: DisjointScanStats = { pairs: 0, pairsPrunedByBbox: 0, vertexPairsCompared: 0 };
  return { isDisjoint: scanDisjoint(parcels, stats), stats };
}

/**
 * Bounding box across all parcels.
 *
 * For an empty input we return a degenerate (0,0,0,0) bbox — callers
 * needing to distinguish "no parcels" from "parcels at the null island"
 * should guard on `parcels.length` first.
 */
export function aggregateBbox(parcels: ReadonlyArray<SiteBoundaryParcel>): { south: number; west: number; north: number; east: number } {
  if (parcels.length === 0) return { south: 0, west: 0, north: 0, east: 0 };
  let south = Number.POSITIVE_INFINITY;
  let north = Number.NEGATIVE_INFINITY;
  let west = Number.POSITIVE_INFINITY;
  let east = Number.NEGATIVE_INFINITY;
  for (const p of parcels) {
    // HOTFIX(lat-crash): `p.polygon` can be undefined (TypeError: not iterable)
    // or hold nullish / non-finite vertices (TypeError: reading 'lat'). Iterate
    // only the cleaned vertices so the bbox can never throw and a NaN vertex
    // can't widen the box to ±Infinity. (Identical bbox for valid parcels.)
    for (const v of cleanVertices(p.polygon)) {
      if (v.lat < south) south = v.lat;
      if (v.lat > north) north = v.lat;
      if (v.lng < west) west = v.lng;
      if (v.lng > east) east = v.lng;
    }
  }
  if (!Number.isFinite(south)) return { south: 0, west: 0, north: 0, east: 0 };
  return { south, west, north, east };
}

/**
 * Compute the `SiteBoundaryAggregate` for a list of parcels.
 *
 * Aggregate centroid is AREA-WEIGHTED:
 *   centroid = Σ(p.area · p.centroid) / Σ(p.area)
 * When `totalArea` is 0 (e.g. all parcels are degenerate), we fall back
 * to the centre of the bounding box so the consumer still gets a usable
 * lat/lng for map framing.
 */
export function computeAggregate(parcels: ReadonlyArray<SiteBoundaryParcel>): SiteBoundaryAggregate {
  const summedArea = sumParcelAreas(parcels);
  const totalArea = unionPolygonAreaAcres(parcels);
  const totalPerimeter = parcels.reduce((s, p) => s + (Number.isFinite(p.perimeter) ? p.perimeter : 0), 0);
  const bbox = aggregateBbox(parcels);
  const hasOverlappingParcels = parcels.length > 1
    && summedArea - totalArea > OVERLAP_AREA_THRESHOLD_ACRES;

  let centroid: { lat: number; lng: number };
  if (summedArea > 0) {
    let lat = 0;
    let lng = 0;
    for (const p of parcels) {
      if (!Number.isFinite(p.area) || p.area <= 0) continue;
      // HOTFIX(lat-crash): `centroid` is typed required, but persisted/legacy
      // KMZ parcels are not runtime-validated and can arrive without one.
      // Reading `.lat`/`.lng` on an undefined centroid threw "Cannot read
      // properties of undefined (reading 'lat')" on every report cover /
      // boundary-map render. Fall back to the polygon centroid (or skip if
      // the polygon is empty too) — guards only, identical maths for valid data.
      const c =
        p.centroid && Number.isFinite(p.centroid.lat) && Number.isFinite(p.centroid.lng)
          ? p.centroid
          : Array.isArray(p.polygon) && p.polygon.length > 0
            ? polygonCentroid(p.polygon)
            : null;
      if (!c) continue;
      lat += c.lat * p.area;
      lng += c.lng * p.area;
    }
    centroid = { lat: lat / summedArea, lng: lng / summedArea };
  } else {
    // Bounding-box centre fallback — keeps map framing usable even when
    // every parcel has zero area (e.g. all degenerate).
    centroid = {
      lat: (bbox.north + bbox.south) / 2,
      lng: (bbox.east + bbox.west) / 2,
    };
  }

  return {
    totalArea,
    totalPerimeter,
    parcelCount: parcels.length,
    aggregateCentroid: centroid,
    bbox,
    isDisjoint: detectDisjoint(parcels),
    hasOverlappingParcels,
    version: SITE_BOUNDARY_AGGREGATE_VERSION,
  };
}

/* ─── Construction helpers ─────────────────────────────────────────── */

/**
 * Build a SiteBoundaryParcel from a polygon + optional metadata. Derived
 * fields (area, perimeter, centroid) are computed; pass overrides in
 * `provided` to use pre-computed values (e.g. from KMZ where the parser
 * already returned area).
 */
export function makeParcel(
  polygon: Array<{ lat: number; lng: number }>,
  options: {
    id: string;
    label?: string;
    holes?: Array<Array<{ lat: number; lng: number }>>;
    parcelId?: string;
    ownership?: string;
    intendedUse?: SiteBoundaryParcel['intendedUse'];
    notes?: string;
    /** Override the derived area (acres) — used by the KMZ import path. */
    area?: number;
    /** Override the derived perimeter (km). */
    perimeter?: number;
    /** Override the derived centroid. */
    centroid?: { lat: number; lng: number };
  },
): SiteBoundaryParcel {
  const holes = cleanHoles(options.holes);
  return {
    id: options.id,
    label: options.label,
    polygon,
    ...(holes.length > 0 ? { holes } : {}),
    area: options.area ?? parcelArea(polygon, holes),
    perimeter: options.perimeter ?? parcelPerimeter(polygon, holes),
    centroid: options.centroid ?? polygonCentroid(polygon),
    parcelId: options.parcelId,
    ownership: options.ownership,
    intendedUse: options.intendedUse,
    notes: options.notes,
  };
}

/**
 * HOTFIX(lat-crash) — SOURCE SANITIZE. Return a runtime-clean copy of a
 * persisted/legacy parcel so EVERY downstream consumer (report cover, site-map,
 * thumbnail, scale bar, prompt blocks) receives a parcel that cannot trigger the
 * "Cannot read properties of undefined (reading 'lat')" crash class:
 *
 *   1. Drop nullish / non-finite polygon vertices.
 *   2. Ensure the centroid is finite — fall back to the polygon centroid of the
 *      CLEANED polygon, then to the bbox centre of the cleaned polygon.
 *   3. Coerce non-finite area / perimeter to finite numbers (recompute from the
 *      cleaned polygon when the persisted value is missing/NaN).
 *
 * Returns `null` when the parcel has < 3 usable vertices (it is not drawable and
 * is dropped by the caller). Already-valid parcels keep their persisted area /
 * perimeter / centroid byte-for-byte — this only repairs malformed input.
 */
export function sanitizeParcel(parcel: SiteBoundaryParcel): SiteBoundaryParcel | null {
  const polygon = cleanVertices(parcel?.polygon);
  if (polygon.length < 3) return null;
  const holes = cleanHoles(parcel?.holes);

  const area = Number.isFinite(parcel.area) ? parcel.area : parcelArea(polygon, holes);
  const perimeter = Number.isFinite(parcel.perimeter) ? parcel.perimeter : parcelPerimeter(polygon, holes);

  let centroid: { lat: number; lng: number };
  if (isFiniteLatLng(parcel.centroid)) {
    centroid = parcel.centroid;
  } else {
    // polygonCentroid is now vertex-safe; it can still return null-island only
    // for an empty polygon, which we've already excluded above. As a final
    // guard, fall back to the cleaned-polygon bbox centre.
    const c = polygonCentroid(polygon);
    if (isFiniteLatLng(c) && (c.lat !== 0 || c.lng !== 0)) {
      centroid = c;
    } else {
      let south = Number.POSITIVE_INFINITY;
      let north = Number.NEGATIVE_INFINITY;
      let west = Number.POSITIVE_INFINITY;
      let east = Number.NEGATIVE_INFINITY;
      for (const v of polygon) {
        if (v.lat < south) south = v.lat;
        if (v.lat > north) north = v.lat;
        if (v.lng < west) west = v.lng;
        if (v.lng > east) east = v.lng;
      }
      centroid = { lat: (north + south) / 2, lng: (east + west) / 2 };
    }
  }

  return {
    ...parcel,
    polygon,
    ...(holes.length > 0 ? { holes } : {}),
    area,
    perimeter,
    centroid,
  };
}

/**
 * Lazy migration on read — when reading a site doc with legacy
 * `siteBoundary` set but no `siteBoundaries[]`, lift the single polygon
 * to one parcel + compute the aggregate. Idempotent: calling on a site
 * that already has `siteBoundaries[]` is a no-op and just returns the
 * existing array. The next write persists the canonical shape.
 *
 * HOTFIX(lat-crash): EVERY returned parcel is run through `sanitizeParcel`
 * (drop nullish/non-finite vertices, drop parcels left with < 3 valid vertices,
 * guarantee a finite centroid) so no downstream render path can dereference an
 * undefined coordinate. For already-valid sites the returned parcels are
 * identical (same polygon, area, perimeter, centroid).
 *
 * Returns the parcels array (possibly empty) — caller writes back into
 * the site object via the back-compat aliases.
 */
export function materializeSiteBoundaries(input: {
  siteBoundaries?: SiteBoundaryParcel[];
  siteBoundary?: Array<{ lat: number; lng: number }>;
  siteArea?: number;
  sitePerimeter?: number;
  siteCentroid?: { lat: number; lng: number };
}): SiteBoundaryParcel[] {
  if (Array.isArray(input.siteBoundaries) && input.siteBoundaries.length > 0) {
    // SOURCE SANITIZE — persisted parcels are NOT runtime-validated; clean each
    // and drop the undrawable (< 3 valid vertices) so consumers are crash-safe.
    return input.siteBoundaries
      .map((p) => sanitizeParcel(p))
      .filter((p): p is SiteBoundaryParcel => p !== null);
  }
  if (Array.isArray(input.siteBoundary) && cleanVertices(input.siteBoundary).length >= 3) {
    const lifted = makeParcel(input.siteBoundary, {
      id: 'p1',
      label: 'Site',
      area: input.siteArea,
      perimeter: input.sitePerimeter,
      centroid: input.siteCentroid,
    });
    const clean = sanitizeParcel(lifted);
    return clean ? [clean] : [];
  }
  return [];
}

/**
 * True when the site has at least one drawable boundary parcel — the same
 * "exportable boundary" predicate SiteMapTab derives inline (each
 * materialized parcel is guaranteed ≥3 sanitized vertices). Shared so the
 * Path-to-GO site-basis step and the map tab agree byte-for-byte on what
 * counts as "boundary present".
 */
export function siteHasBoundary(input: {
  siteBoundaries?: SiteBoundaryParcel[];
  siteBoundary?: Array<{ lat: number; lng: number }>;
  siteArea?: number;
  sitePerimeter?: number;
  siteCentroid?: { lat: number; lng: number };
}): boolean {
  return materializeSiteBoundaries(input).length > 0;
}

/**
 * Generate the next stable parcel id for a list of parcels. Format:
 * `p1`, `p2`, … — monotonically increasing past the highest existing
 * `pN` id; non-numeric ids are ignored when picking the next number.
 * This keeps the legacy single-parcel `'p1'` migration identity stable
 * across reads.
 */
export function nextParcelId(parcels: ReadonlyArray<SiteBoundaryParcel>): string {
  let max = 0;
  for (const p of parcels) {
    const m = /^p(\d+)$/.exec(p.id);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
  }
  return `p${max + 1}`;
}

/* ─── Constants re-exported for callers (so they don't redefine) ───── */

export const SITE_BOUNDARY_GEOMETRY_CONSTANTS = {
  EARTH_RADIUS_M,
  DEG_TO_RAD,
  M_PER_KM,
  M2_PER_ACRE,
  DISJOINT_THRESHOLD_M,
  BBOX_PREFILTER_SLACK_M,
} as const;
